/**
 * Wake-word listener using the browser's native Web Speech API.
 * Free, no API key, runs in Chrome/Edge/Safari out of the box.
 *
 * Phrases matched: "hey aria", "okay aria", "ok aria", "aria", "aria are you there"
 * (case-insensitive). On a hit, calls `onWake()`.
 *
 * The listener auto-restarts on `end` so it stays always-on as long as
 * `enabled === true`. We stop it on disable / unmount and on a successful hit
 * (so we don't double-fire while ConversationMode is opening). Caller is
 * responsible for re-enabling once the conversation flow completes.
 */
import { useEffect, useRef, useState } from "react";

const PHRASES = [
  /\bhey\s+aria\b/i,
  /\bok(?:ay)?\s+aria\b/i,
  /\bhi\s+aria\b/i,
  /\baria,?\s+are\s+you\s+there\b/i,
  /\baria,?\s+wake\s+up\b/i,
  /\baria\b(?=[\s,.!?]|$)/i,   // bare "aria" as a whole word
];

export default function useWakeWord({ enabled, onWake }) {
  const [supported, setSupported] = useState(true);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const wantRunningRef = useRef(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }

    if (!enabled) {
      wantRunningRef.current = false;
      try { recRef.current?.stop(); } catch (_) { /* already stopped — harmless */ }
      setActive(false);
      return;
    }

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.maxAlternatives = 1;

    rec.onstart = () => setActive(true);
    rec.onerror = (e) => {
      // 'not-allowed' = mic permission denied; 'no-speech' = harmless silence
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        wantRunningRef.current = false;
        setError("Microphone permission denied");
      } else if (e.error === "audio-capture") {
        wantRunningRef.current = false;
        setError("No microphone detected");
      }
    };
    rec.onend = () => {
      setActive(false);
      // Auto-restart unless we explicitly stopped
      if (wantRunningRef.current) {
        try { rec.start(); } catch (_) { /* benign: SR throws if start() races with end(); next onend retries */ }
      }
    };

    rec.onresult = (ev) => {
      // Scan only the newest results — both interim and final — for the wake phrase
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const transcript = (ev.results[i][0]?.transcript || "").trim();
        if (!transcript) continue;
        for (const pat of PHRASES) {
          if (pat.test(transcript)) {
            // Stop the listener — caller will re-enable later
            wantRunningRef.current = false;
            try { rec.stop(); } catch (_) { /* already stopped — harmless */ }
            setActive(false);
            onWake?.(transcript);
            return;
          }
        }
      }
    };

    recRef.current = rec;
    wantRunningRef.current = true;
    try { rec.start(); } catch (_) { /* benign: SR may be mid-start; onend will retry */ }

    return () => {
      wantRunningRef.current = false;
      try { rec.stop(); } catch (_) { /* already stopped — harmless */ }
      recRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { supported, active, error };
}
